// Normalises a URL-ish env value so a bare domain like "www.lexvio.ai" doesn't
// blow up `new URL(...)` at build time (Amplify config var gotcha).
function normalizeUrl(raw: string | undefined, fallback: string): string {
  const v = (raw ?? "").trim();
  if (!v) return fallback;
  if (/^https?:\/\//i.test(v)) return v.replace(/\/+$/, "");
  return `https://${v}`.replace(/\/+$/, "");
}

// The canonical host is www — the apex 301-redirects to it. A canonical or
// og:url pointing at the apex therefore points at a redirect, so the fallback
// must be the www form, not the bare domain.
//
// 2026-08-31: www.global.lexvio.ai is the CANONICAL global host (decision on
// record — the hyphenated global-lexvio.ai has no DNS and survives only as a
// legacy alias in the backend's edition-domain list). The backend's
// edition-domain, CORS and GLOBAL_APP_BASE_URL defaults were aligned in the
// same change (GLOBAL_READINESS_AUDIT_2026-08-31.md §A4).
export const SITE_URL = normalizeUrl(process.env.NEXT_PUBLIC_SITE_URL, "https://www.global.lexvio.ai");
// Post-merge the product app and the marketing site are one Next app on one
// origin, so APP_URL's fallback is the site origin (kept separate in case the
// app is ever split back out to its own subdomain).
export const APP_URL = normalizeUrl(process.env.NEXT_PUBLIC_APP_URL, "https://www.global.lexvio.ai");

/**
 * Which of the two products this frontend is. Not a preference — this repo is
 * global-lexvio.ai, the same way SITE_URL above is.
 *
 * The backend stamps an org's edition from the domain the browser was on, and
 * a recognised Origin always beats this value, so in production it is inert.
 * It matters in development: `localhost` matches neither the global nor the
 * India domain list, so edition resolution falls through to the backend's
 * `default_edition`, which is "india" — and an India org has its jurisdiction
 * pinned to IN and its currency to INR by `editions.org_defaults`, discarding
 * whatever country the visitor chose at signup. Sending it explicitly is the
 * documented fallback for callers whose origin the backend cannot read.
 */
export const APP_EDITION = "global" as const;
// Fallback differs by NODE_ENV — localhost in dev, prod API otherwise — so a
// missing env var in production still hits the real backend instead of throwing
// ECONNREFUSED against localhost:8000.
export const API_BASE =
  process.env.LEXAI_API_BASE ||
  // api.lexvio.ai is the one live API host (shared by both editions; the
  // backend treats it as edition-neutral) — api.global-lexvio.ai has no DNS.
  (process.env.NODE_ENV === "production" ? "https://api.lexvio.ai" : "http://localhost:8000");
